import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'

/** Détail d'erreur FastAPI (string ou tableau de validation). */
function extractApiDetail(err: any): string {
  const d = err?.response?.data?.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d) && d.length && typeof d[0]?.msg === 'string') return d[0].msg
  return ''
}

interface AgentMCPDrawerProps {
  open: boolean
  onClose: () => void
  agentId: number | null
  agentName: string
}

interface MCPConnection {
  id: number
  display_name: string
  is_active: boolean
  /** Présent sur GET /mcp/connections : agents déjà liés à cette connexion */
  linked_agents?: Array<{
    id: number
    agent_id: number
    user_mcp_connection_id: number
    is_active: boolean
    created_at: string
  }>
  mcp_server: {
    id: number
    name: string
    slug: string
    category: string
  }
}

interface AgentMCPLink {
  id: number
  is_active: boolean
  user_mcp_connection: MCPConnection
  tools_available: Array<{
    name: string
    description: string
  }>
}

export default function AgentMCPDrawer({ open, onClose, agentId, agentName }: AgentMCPDrawerProps) {
  const [loading, setLoading] = useState(false)
  const [availableConnections, setAvailableConnections] = useState<MCPConnection[]>([])
  const [linkedConnections, setLinkedConnections] = useState<AgentMCPLink[]>([])
  const [error, setError] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)

  useEffect(() => {
    if (open && agentId) {
      loadData()
    }
  }, [open, agentId])

  const loadData = async () => {
    if (!agentId) return
    
    try {
      setLoading(true)
      setError(null)
      setInfoMessage(null)
      
      // Load available connections
      const connectionsRes = await api.get('/mcp/connections')
      setAvailableConnections(connectionsRes.data.items || [])
      
      // Load agent's linked connections
      const linksRes = await api.get(`/mcp/agents/${agentId}/mcp`, {
        params: { include_tools: false },
      })
      setLinkedConnections(linksRes.data || [])
    } catch (err: any) {
      const detail = extractApiDetail(err)
      setError(detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const handleLink = async (connectionId: number) => {
    if (!agentId) return
    
    try {
      setError(null)
      setInfoMessage(null)
      await api.post(`/mcp/agents/${agentId}/mcp`, {
        user_mcp_connection_id: connectionId,
        is_active: true
      })
      await loadData()
      setInfoMessage('Connexion MCP liée à cet agent.')
    } catch (err: any) {
      const detail = extractApiDetail(err)
      const already =
        err.response?.status === 400 &&
        (detail === 'Link already exists' || /already exists/i.test(detail))
      if (already) {
        await loadData()
        setInfoMessage('Cette connexion était déjà liée à cet agent — liste mise à jour.')
        return
      }
      setError(detail || 'Erreur lors de la liaison')
    }
  }

  const handleUnlink = async (connectionId: number) => {
    if (!agentId) return
    
    try {
      setError(null)
      setInfoMessage(null)
      await api.delete(`/mcp/agents/${agentId}/mcp/${connectionId}`)
      await loadData()
    } catch (err: any) {
      const detail = extractApiDetail(err)
      setError(detail || 'Erreur lors de la suppression')
    }
  }

  if (!open) return null

  const linkedIdsFromAgentEndpoint = linkedConnections.map((link) => link.user_mcp_connection.id)
  const linkedIdsFromConnectionsIndex = availableConnections
    .filter((conn) => conn.linked_agents?.some((la) => la.agent_id === agentId))
    .map((conn) => conn.id)
  const linkedConnectionIds = [...new Set([...linkedIdsFromAgentEndpoint, ...linkedIdsFromConnectionsIndex])]
  const unlinkedConnections = availableConnections.filter((conn) => !linkedConnectionIds.includes(conn.id))

  /** Si GET /agents/:id/mcp échoue ou est vide alors que la liaison existe (vu via linked_agents sur /connections). */
  const linkedOnlyFromConnectionsIndex: AgentMCPLink[] = availableConnections
    .filter((conn) => conn.linked_agents?.some((la) => la.agent_id === agentId))
    .filter((conn) => !linkedIdsFromAgentEndpoint.includes(conn.id))
    .map((conn) => {
      const la = conn.linked_agents!.find((x) => x.agent_id === agentId)!
      return {
        id: la.id,
        is_active: la.is_active,
        user_mcp_connection: conn,
        tools_available: [] as AgentMCPLink['tools_available'],
      }
    })

  const displayLinkedConnections = [...linkedConnections, ...linkedOnlyFromConnectionsIndex]

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-1/2 lg:w-1/3 bg-white dark:bg-zinc-900 shadow-2xl z-50 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                🔌 Connexions MCP
              </h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                Agent : <span className="font-medium">{agentName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {infoMessage && (
            <div className="mb-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded text-emerald-800 dark:text-emerald-200 text-sm">
              {infoMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : (
            <>
              {/* Linked Connections */}
              {displayLinkedConnections.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-3">
                    Connexions actives ({displayLinkedConnections.length})
                  </h3>
                  <div className="space-y-2">
                    {displayLinkedConnections.map((link) => (
                      <div
                        key={link.id}
                        className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-zinc-900 dark:text-zinc-100">
                              {link.user_mcp_connection.display_name}
                            </h4>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">
                              {link.user_mcp_connection.mcp_server.name}
                            </p>
                          </div>
                          <button
                            onClick={() => handleUnlink(link.user_mcp_connection.id)}
                            className="px-3 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                          >
                            Délier
                          </button>
                        </div>
                        {link.tools_available && link.tools_available.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800">
                            <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                              {link.tools_available.length} outil{link.tools_available.length > 1 ? 's' : ''} disponible{link.tools_available.length > 1 ? 's' : ''}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {link.tools_available.slice(0, 3).map((tool, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded"
                                  title={tool.description}
                                >
                                  {tool.name}
                                </span>
                              ))}
                              {link.tools_available.length > 3 && (
                                <span className="px-2 py-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                                  +{link.tools_available.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Connections */}
              {unlinkedConnections.length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-3">
                    Connexions disponibles ({unlinkedConnections.length})
                  </h3>
                  <div className="space-y-2">
                    {unlinkedConnections.map((conn) => (
                      <div
                        key={conn.id}
                        className="p-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-zinc-900 dark:text-zinc-100">
                              {conn.display_name}
                            </h4>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">
                              {conn.mcp_server.name}
                            </p>
                          </div>
                          <button
                            onClick={() => handleLink(conn.id)}
                            className="px-3 py-1 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors"
                          >
                            Lier
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : linkedConnectionIds.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-4xl mb-4 block">🔌</span>
                  <p className="text-zinc-600 dark:text-zinc-400 mb-4">
                    Aucune connexion MCP disponible
                  </p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-500">
                    Créez d'abord des connexions MCP dans<br />
                    Menu → Intégrations → MCP
                  </p>
                </div>
              ) : (
                <p className="text-sm text-zinc-600 dark:text-zinc-400 text-center py-4">
                  Toutes vos connexions sont déjà liées à cet agent
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
